/// <amd-module name="Wit/Common/DateUtils" />
import { startsWith } from "VSS/Core/Util/String";
import { getCurrentCulture, getInvariantCulture, ICultureInfo, IDateTimeFormatSettings } from "./Culture";
import { getPageContext } from "VSS/Platform/Context";

const minute = 1000 * 60;
const hour = minute * 60;
const day = hour * 24;
const week = day * 7;
export const MacroToday = "@today";

/**
 * Tooltip time format to hide timezone info, because currently used Date doesn't have methods to change it
 */
export const tooltipTimeFormat: Intl.DateTimeFormatOptions = { hour: "numeric", minute: "numeric" };

/**
 * Build a date object with the specified object.
 * As of now, number and string types are recognized.
 *
 * @param value The value to be converted to a Date object
 */
export function buildDate(value: Date | string): Date {
    let date: Date = new Date();
    if (value instanceof Date) {
        date = value;
    } else if (typeof value === "string") {
        var dateString: string = value;
        dateString = dateString.trim();
        if (startsWith(dateString, MacroToday, /* ignore case */ true)) {
            dateString = dateString.replace(/\s/g, "").substr(MacroToday.length);

            // The macro can be as follows : @Today , @Today + Days , @Today - Days
            if (dateString !== "") {
                let match = dateString.match(/([+-])(\d+)/);
                if (match) {
                    var addedDays: number = parseInt(match[2]);
                    if (match[1] === "-") {
                        addedDays *= -1;
                    }
                    date.setDate(date.getDate() + addedDays);
                }
            }
        } else {
            const parsedDate: Date | undefined = parseDateString(dateString);
            if (parsedDate) {
                date = parsedDate;
            }
        }
    }

    return date;
}

export function daysBetweenDates(startDate: Date, endDate: Date, exclusive?: boolean): number {
    startDate = new Date(startDate.getTime());
    startDate.setHours(0, 0, 0, 0);

    endDate = new Date(endDate.getTime());
    endDate.setHours(0, 0, 0, 0);

    return Math.round(Math.abs(endDate.getTime() - startDate.getTime()) / day) + (exclusive === true ? 0 : 1);
}

export function shiftToLocal(date: Date): Date {
    return new Date(date.getTime() - date.getTimezoneOffset() * minute);
}

export function shiftToUTC(localDate: Date): Date {
    return new Date(
        localDate.getUTCFullYear(),
        localDate.getUTCMonth(),
        localDate.getUTCDate(),
        localDate.getUTCHours(),
        localDate.getUTCMinutes(),
        localDate.getUTCSeconds(),
        localDate.getUTCMilliseconds()
    );
}

const isoDateRegex = /^(\d{4}|\+{1}\d{6})-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,7})?(Z|[+-]\d{2}:\d{2})?$/;
export function isIsoDate(dateString: string): boolean {
    return isoDateRegex.test(dateString);
}

export function parseDateString(dateString: string, parseTime?: boolean): Date | undefined {
    let date: Date | undefined;
    let year: number;

    // Attempt to parse with the parse format
    if (parseTime) {
        date = MicrosoftAjaxDateFormatting.parseTimeString(dateString);
    } else {
        date = MicrosoftAjaxDateFormatting.parseDateString(dateString, getCurrentCulture());
    }

    // If the date could not be parsed with the parse format parse it directly if it's in ISO format
    if (isIsoDate(dateString)) {
        date = new Date(dateString);
    }

    if (!(date instanceof Date) || Number.isNaN(date as any)) {
        date = undefined;
    } else {
        // Javascript parses 2-digit years as 19xx, but we want 2-digit years
        // to map to the current century. Examine the year, and if it is in
        // the 1900's, then correct the century unless the date was entered
        // with the '19' prefix.
        year = date.getFullYear();
        if (year >= 1900 && year < 2000) {
            if (dateString.indexOf(year.toString()) < 0) {
                // The year is 19xx but 19xx does not appear in the date string
                // so set the year to the current century.
                date.setFullYear((year % 100) + Math.floor(new Date().getFullYear() / 100) * 100);
            }
        }
    }

    return date;
}

export function dateToString(value: Date, useLocale?: boolean, format?: string): string {
    return MicrosoftAjaxDateFormatting.dateToString(value, useLocale, format);
}

export function tryConvertStringToDate(value: string, parameters?: {currentCulture?: ICultureInfo, format?: string}): Date | undefined {
    return MicrosoftAjaxDateFormatting.tryConvertStringToDate(value, parameters);
}

// Copied from Vssf\WebPlatform\Platform\Scripts\VSS\Utils\String.ts
namespace MicrosoftAjaxDateFormatting {
    function _getDateEra(date: Date, eras: any[]): number {
        if (!eras) {
            return 0;
        }
        let start;
        const ticks = date.getTime();
        for (let i = 0, l = eras.length; i < l; i += 4) {
            start = eras[i + 2];
            if (start === null || ticks >= start) {
                return i;
            }
        }
        return 0;
    }

    function _getDateEraYear(date: Date, dtf: IDateTimeFormatSettings, era: number, sortable?: boolean) {
        let year = date.getFullYear();

        // If we have a locale conversion function then we need to use it to get the year before applying the era
        const convert = dtf.Calendar.convert;
        if (convert) {
            year = convert.fromGregorian(date)[0];
        }

        if (!sortable && dtf.eras) {
            year -= dtf.eras[era + 3];
        }
        return year;
    }

    function _expandDateFormat(dtf: IDateTimeFormatSettings, format: string) {
        if (!format) {
            format = "F";
        }
        const len = format.length;
        if (len === 1) {
            switch (format) {
                case "d":
                    return dtf.ShortDatePattern;
                case "D":
                    return dtf.LongDatePattern;
                case "g":
                    return dtf.ShortDatePattern + " " + dtf.ShortTimePattern;
                case "G":
                    return dtf.ShortDatePattern + " " + dtf.LongTimePattern;
                case "t":
                    return dtf.ShortTimePattern;
                case "T":
                    return dtf.LongTimePattern;
                case "f":
                    return dtf.LongDatePattern + " " + dtf.ShortTimePattern;
                case "F":
                    return dtf.FullDateTimePattern;
                case "M":
                case "m":
                    return dtf.MonthDayPattern;
                case "s":
                    return dtf.SortableDateTimePattern;
                case "Y":
                case "y":
                    return dtf.YearMonthPattern;
                case "R":
                case "r":
                    return dtf.RFC1123Pattern;
                case "u":
                    return dtf.UniversalSortableDateTimePattern;
                default:
                    throw new Error("Input string was not in a correct format.");
            }
        } else if (len === 2 && format.charAt(0) === "%") {
            format = format.charAt(1);
        }
        return format;
    }

    export function dateToString(value: Date, useLocale?: boolean, format?: string): string {
        const dtf = useLocale ? getCurrentCulture().dateTimeFormat : getInvariantCulture().dateTimeFormat;
        const convert = dtf.Calendar.convert;

        if (!format || !format.length || format === "i") {
            if (useLocale) {
                if (convert) {
                    format = dtf.FullDateTimePattern;
                } else {
                    const eraDate = new Date(value.getTime());
                    const era = _getDateEra(value, dtf.eras);
                    eraDate.setFullYear(_getDateEraYear(value, dtf, era));
                    return eraDate.toLocaleString();
                }
            } else {
                return value.toString();
            }
        }
        const eras = dtf.eras;
        const sortable = format === "s";
        format = _expandDateFormat(dtf, format);
        const ret: string[] = [];
        let hour;
        function addLeadingZero(num: number) {
            if (num < 10) {
                return "0" + num;
            }
            return num.toString();
        }
        function addLeadingZeros(num: number) {
            if (num < 10) {
                return "00" + num;
            }
            if (num < 100) {
                return "0" + num;
            }
            return num.toString();
        }
        function padYear(year: number) {
            if (year < 10) {
                return "000" + year;
            } else if (year < 100) {
                return "00" + year;
            } else if (year < 1000) {
                return "0" + year;
            }
            return year.toString();
        }
        function _appendPreOrPostMatch(preMatch: string, result: string[]) {
            let quoteCount = 0,
                escaped = false;
            for (let i = 0, il = preMatch.length; i < il; i++) {
                const c = preMatch.charAt(i);
                switch (c) {
                    case "'":
                        if (escaped) {
                            result.push("'");
                        } else {
                            quoteCount++;
                        }
                        escaped = false;
                        break;
                    case "\\":
                        if (escaped) {
                            result.push("\\");
                        }
                        escaped = !escaped;
                        break;
                    default:
                        result.push(c);
                        escaped = false;
                        break;
                }
            }
            return quoteCount;
        }

        let foundDay: boolean, checkedDay: boolean;
        const dayPartRegExp = /([^d]|^)(d|dd)([^d]|$)/g;
        function hasDay() {
            if (foundDay || checkedDay) {
                return foundDay;
            }
            foundDay = format ? dayPartRegExp.test(format) : false;
            checkedDay = true;
            return foundDay;
        }

        let quoteCount = 0,
            converted: number[] = [];
        const tokenRegExp = /\/|dddd|ddd|dd|d|MMMM|MMM|MM|M|yyyy|yy|y|hh|h|HH|H|mm|m|ss|s|tt|t|fff|ff|f|zzz|zz|z|gg|g/g;

        if (!sortable && convert) {
            converted = convert.fromGregorian(value);
        }
        function getPart(date: any, part: number) {
            if (converted.length !== 0) {
                return converted[part];
            }
            switch (part) {
                case 0:
                    return date.getFullYear();
                case 1:
                    return date.getMonth();
                case 2:
                    return date.getDate();
            }
        }
        for (;;) {
            const index = tokenRegExp.lastIndex;
            const ar = tokenRegExp.exec(format);
            const preMatch = format.slice(index, ar ? ar.index : format.length);
            quoteCount += _appendPreOrPostMatch(preMatch, ret);
            if (!ar) {
                break;
            }
            if (quoteCount % 2 === 1) {
                ret.push(ar[0]);
                continue;
            }

            switch (ar[0]) {
                case "dddd":
                    ret.push(dtf.DayNames[value.getDay()]);
                    break;
                case "ddd":
                    ret.push(dtf.AbbreviatedDayNames[value.getDay()]);
                    break;
                case "dd":
                    foundDay = true;
                    ret.push(addLeadingZero(getPart(value, 2)));
                    break;
                case "d":
                    foundDay = true;
                    ret.push(getPart(value, 2));
                    break;
                case "MMMM":
                    ret.push(
                        dtf.MonthGenitiveNames && dtf.MonthGenitiveNames.length !== 0 && hasDay()
                            ? dtf.MonthGenitiveNames[getPart(value, 1)]
                            : dtf.MonthNames[getPart(value, 1)]
                    );
                    break;
                case "MMM":
                    ret.push(
                        dtf.AbbreviatedMonthGenitiveNames && dtf.AbbreviatedMonthGenitiveNames.length !== 0 && hasDay()
                            ? dtf.AbbreviatedMonthGenitiveNames[getPart(value, 1)]
                            : dtf.AbbreviatedMonthNames[getPart(value, 1)]
                    );
                    break;
                case "MM":
                    ret.push(addLeadingZero(getPart(value, 1) + 1));
                    break;
                case "M":
                    ret.push(getPart(value, 1) + 1);
                    break;
                case "yyyy":
                    ret.push(padYear(converted.length !== 0 ? converted[0] : _getDateEraYear(value, dtf, _getDateEra(value, eras), sortable)));
                    break;
                case "yy":
                    ret.push(
                        addLeadingZero(
                            (converted.length !== 0 ? converted[0] : _getDateEraYear(value, dtf, _getDateEra(value, eras), sortable)) % 100
                        )
                    );
                    break;
                case "y":
                    ret.push(
                        ((converted.length !== 0 ? converted[0] : _getDateEraYear(value, dtf, _getDateEra(value, eras), sortable)) % 100).toString()
                    );
                    break;
                case "hh":
                    hour = value.getHours() % 12;
                    if (hour === 0) {
                        hour = 12;
                    }
                    ret.push(addLeadingZero(hour));
                    break;
                case "h":
                    hour = value.getHours() % 12;
                    if (hour === 0) {
                        hour = 12;
                    }
                    ret.push(hour.toString());
                    break;
                case "HH":
                    ret.push(addLeadingZero(value.getHours()));
                    break;
                case "H":
                    ret.push(value.getHours().toString());
                    break;
                case "mm":
                    ret.push(addLeadingZero(value.getMinutes()));
                    break;
                case "m":
                    ret.push(value.getMinutes().toString());
                    break;
                case "ss":
                    ret.push(addLeadingZero(value.getSeconds()));
                    break;
                case "s":
                    ret.push(value.getSeconds().toString());
                    break;
                case "tt":
                    ret.push(value.getHours() < 12 ? dtf.AMDesignator : dtf.PMDesignator);
                    break;
                case "t":
                    ret.push((value.getHours() < 12 ? dtf.AMDesignator : dtf.PMDesignator).charAt(0));
                    break;
                case "f":
                    ret.push(addLeadingZeros(value.getMilliseconds()).charAt(0));
                    break;
                case "ff":
                    ret.push(addLeadingZeros(value.getMilliseconds()).substr(0, 2));
                    break;
                case "fff":
                    ret.push(addLeadingZeros(value.getMilliseconds()));
                    break;
                case "z":
                    hour = value.getTimezoneOffset() / 60;
                    ret.push((hour <= 0 ? "+" : "-") + Math.floor(Math.abs(hour)));
                    break;
                case "zz":
                    hour = value.getTimezoneOffset() / 60;
                    ret.push((hour <= 0 ? "+" : "-") + addLeadingZero(Math.floor(Math.abs(hour))));
                    break;
                case "zzz":
                    hour = value.getTimezoneOffset() / 60;
                    ret.push(
                        (hour <= 0 ? "+" : "-") +
                            addLeadingZero(Math.floor(Math.abs(hour))) +
                            ":" +
                            addLeadingZero(Math.abs(value.getTimezoneOffset() % 60))
                    );
                    break;
                case "g":
                case "gg":
                    if (dtf.eras) {
                        ret.push(dtf.eras[_getDateEra(value, eras) + 1]);
                    }
                    break;
                case "/":
                    ret.push(dtf.DateSeparator);
                    break;
                default:
                    new Error("Invalid date format pattern");
                    break;
            }
        }
        return ret.join("");
    }

    /**
     * convert a string to a date
     * @param value string to convert      
     * @param parameters parameters to use for conversion    
     * @returns Date object if conversion was successful, undefined otherwise  
     */
    export function tryConvertStringToDate(value: string, parameters?: {currentCulture?: ICultureInfo, format?: string}): Date | undefined {
        if (!parameters || !parameters.currentCulture) {
            return parseDateString(value, getInvariantCulture());
        }

        const formatIsNotSet = !parameters.format || !parameters.format.length || parameters.format === "i";
        
        const dtf = parameters.currentCulture.dateTimeFormat;
        const convert = dtf.Calendar.convert;

        if ((formatIsNotSet && !convert) || !parameters.format) {
            return parseDateString(value, parameters.currentCulture);
        }

        const formatToUse = formatIsNotSet ? dtf.FullDateTimePattern : _expandDateFormat(dtf, parameters.format);
      
        const date = _parseExact(value, formatToUse, parameters.currentCulture);
        return date ?? undefined;
    }

    export function parseDateString(value: string, cultureInfo: ICultureInfo): Date | undefined {
        const allFormats = _getDateTimeFormats(cultureInfo.dateTimeFormat);
        for (let i = 0, l = allFormats.length; i < l; i++) {
            const date = _parseExact(value, allFormats[i], cultureInfo);
            if (date) {
                return date;
            }
        }
        return undefined;
    }

    function _getDateTimeFormats(dtf: IDateTimeFormatSettings) {
        return [
            dtf.MonthDayPattern,
            dtf.YearMonthPattern,
            dtf.ShortDatePattern,
            dtf.ShortTimePattern,
            dtf.LongDatePattern,
            dtf.LongTimePattern,
            dtf.FullDateTimePattern,
            dtf.RFC1123Pattern,
            dtf.SortableDateTimePattern,
            dtf.UniversalSortableDateTimePattern,
            `${dtf.ShortDatePattern} ${dtf.LongTimePattern}`,
            `${dtf.LongDatePattern} ${dtf.ShortTimePattern}`,
            `${dtf.ShortDatePattern} ${dtf.ShortTimePattern}`
        ];
    }

    export function parseTimeString(time: string): Date | undefined {
        if (!time?.trim()) {
            return undefined;
        }
        const timeRegExp = new RegExp(/^(\b[0-9]\b|\b[0-1][0-9]\b|\b2[0-3]\b):(\b[0-9]\b|\b[0-5][0-9]\b)/);
        const match = time.match(timeRegExp);
        if (match) {
            let hours = parseInt(match[1]);
            const minutes = parseInt(match[2]);
            const ampm = time.substring(match[0].length, time.length).trim().toLocaleLowerCase();
            if (ampm) {
                if (hours <= 12) {
                    if (ampm === "am") {
                        if (hours === 12) {
                            hours = 0;
                        }
                    } else if (ampm === "pm") {
                        if (hours < 12) {
                            hours += 12;
                        }
                    } else {
                        return undefined;
                    }
                } else {
                    return undefined;
                }
            }
            const date = new Date();
            date.setHours(hours);
            date.setMinutes(minutes);
            return date;
        }
        return undefined;
    }

    function _parseExact(value: string, format: string, cultureInfo: ICultureInfo) {
        value = value.trim();
        const dtf = cultureInfo.dateTimeFormat;
        const parseInfo = _getParseRegExp(dtf, format);
        const match = new RegExp(parseInfo.regExp).exec(value);
        if (match === null) {
            return null;
        }

        const groups = parseInfo.groups;
        let era = null,
            year = null,
            month = null,
            date = null,
            weekDay = null,
            hour = 0,
            hourOffset,
            min = 0,
            sec = 0,
            msec = 0,
            tzMinOffset = null,
            pmHour = false;
        for (let j = 0, jl = groups.length; j < jl; j++) {
            const matchGroup = match[j + 1];
            if (matchGroup) {
                switch (groups[j]) {
                    case "dd":
                    case "d":
                        date = parseInt(matchGroup, 10);
                        if (date < 1 || date > 31) {
                            return null;
                        }
                        break;
                    case "MMMM":
                        month = _getMonthIndex(cultureInfo, matchGroup);
                        if (month < 0 || month > 11) {
                            return null;
                        }
                        break;
                    case "MMM":
                        month = _getAbbrMonthIndex(cultureInfo, matchGroup);
                        if (month < 0 || month > 11) {
                            return null;
                        }
                        break;
                    case "M":
                    case "MM":
                        month = parseInt(matchGroup, 10) - 1;
                        if (month < 0 || month > 11) {
                            return null;
                        }
                        break;
                    case "y":
                    case "yy":
                        year = _expandYear(dtf, parseInt(matchGroup, 10));
                        if (year < 0 || year > 9999) {
                            return null;
                        }
                        break;
                    case "yyyy":
                        year = parseInt(matchGroup, 10);
                        if (year < 0 || year > 9999) {
                            return null;
                        }
                        break;
                    case "h":
                    case "hh":
                        hour = parseInt(matchGroup, 10);
                        if (hour === 12) {
                            hour = 0;
                        }
                        if (hour < 0 || hour > 11) {
                            return null;
                        }
                        break;
                    case "H":
                    case "HH":
                        hour = parseInt(matchGroup, 10);
                        if (hour < 0 || hour > 23) {
                            return null;
                        }
                        break;
                    case "m":
                    case "mm":
                        min = parseInt(matchGroup, 10);
                        if (min < 0 || min > 59) {
                            return null;
                        }
                        break;
                    case "s":
                    case "ss":
                        sec = parseInt(matchGroup, 10);
                        if (sec < 0 || sec > 59) {
                            return null;
                        }
                        break;
                    case "tt":
                    case "t":
                        const upperToken = matchGroup.toUpperCase();
                        pmHour = upperToken === dtf.PMDesignator.toUpperCase();
                        if (!pmHour && upperToken !== dtf.AMDesignator.toUpperCase()) {
                            return null;
                        }
                        break;
                    case "f":
                        msec = parseInt(matchGroup, 10) * 100;
                        if (msec < 0 || msec > 999) {
                            return null;
                        }
                        break;
                    case "ff":
                        msec = parseInt(matchGroup, 10) * 10;
                        if (msec < 0 || msec > 999) {
                            return null;
                        }
                        break;
                    case "fff":
                        msec = parseInt(matchGroup, 10);
                        if (msec < 0 || msec > 999) {
                            return null;
                        }
                        break;
                    case "dddd":
                        weekDay = _getDayIndex(cultureInfo, matchGroup);
                        if (weekDay < 0 || weekDay > 6) {
                            return null;
                        }
                        break;
                    case "ddd":
                        weekDay = _getAbbrDayIndex(cultureInfo, matchGroup);
                        if (weekDay < 0 || weekDay > 6) {
                            return null;
                        }
                        break;
                    case "zzz":
                        const offsets = matchGroup.split(/:/);
                        if (offsets.length !== 2) {
                            return null;
                        }
                        hourOffset = parseInt(offsets[0], 10);
                        if (hourOffset < -12 || hourOffset > 13) {
                            return null;
                        }
                        const minOffset = parseInt(offsets[1], 10);
                        if (minOffset < 0 || minOffset > 59) {
                            return null;
                        }
                        tzMinOffset = hourOffset * 60 + (matchGroup.startsWith("-") ? -minOffset : minOffset);
                        break;
                    case "z":
                    case "zz":
                        hourOffset = parseInt(matchGroup, 10);
                        if (hourOffset < -12 || hourOffset > 13) {
                            return null;
                        }
                        tzMinOffset = hourOffset * 60;
                        break;
                    case "g":
                    case "gg":
                        let eraName = matchGroup;
                        if (!eraName || !dtf.eras) {
                            return null;
                        }
                        eraName = eraName.toLowerCase().trim();
                        for (let i = 0, l = dtf.eras.length; i < l; i += 4) {
                            if (eraName === dtf.eras[i + 1].toLowerCase()) {
                                era = i;
                                break;
                            }
                        }
                        if (era === null) {
                            return null;
                        }
                        break;
                }
            }
        }
        let result = new Date(),
            defaultYear;
        const convert = dtf.Calendar.convert;
        if (convert) {
            defaultYear = convert.fromGregorian(result)[0];
        } else {
            defaultYear = result.getFullYear();
        }
        if (year === null) {
            year = defaultYear;
        } else if (dtf.eras) {
            year += dtf.eras[(era || 0) + 3];
        }
        if (month === null) {
            month = 0;
        }
        if (date === null) {
            date = 1;
        }
        if (convert) {
            result = convert.toGregorian(year, month, date);
            if (result === null) {
                return null;
            }
        } else {
            result.setFullYear(year, month, date);
            if (result.getDate() !== date) {
                return null;
            }
            if (weekDay !== null && result.getDay() !== weekDay) {
                return null;
            }
        }
        if (pmHour && hour < 12) {
            hour += 12;
        }
        result.setHours(hour, min, sec, msec);
        if (tzMinOffset !== null) {
            const adjustedMin = result.getMinutes() - (tzMinOffset + result.getTimezoneOffset());
            result.setHours(result.getHours() + parseInt((adjustedMin / 60) as any, 10), adjustedMin % 60);
        }
        return result;
    }

    function _appendPreOrPostMatch(preMatch: string, result: string[]) {
        let quoteCount = 0,
            escaped = false;
        for (let i = 0, il = preMatch.length; i < il; i++) {
            const c = preMatch.charAt(i);
            switch (c) {
                case "'":
                    if (escaped) {
                        result.push("'");
                    } else {
                        quoteCount++;
                    }
                    escaped = false;
                    break;
                case "\\":
                    if (escaped) {
                        result.push("\\");
                    }
                    escaped = !escaped;
                    break;
                default:
                    result.push(c);
                    escaped = false;
                    break;
            }
        }
        return quoteCount;
    }

    function _expandYear(dtf: IDateTimeFormatSettings, year: number) {
        const now = new Date(),
            era = _getDateEra(now, dtf.eras);

        if (year < 100) {
            const curr = _getDateEraYear(now, dtf, era);
            year += curr - (curr % 100);
            if (year > dtf.Calendar.TwoDigitYearMax) {
                year -= 100;
            }
        }
        return year;
    }

    function _getParseRegExp(dtf: IDateTimeFormatSettings, format: string) {
        if (!(dtf as any)._parseRegExp) {
            (dtf as any)._parseRegExp = {};
        } else if ((dtf as any)._parseRegExp[format]) {
            return (dtf as any)._parseRegExp[format];
        }

        let expFormat = _expandDateFormat(dtf, format);
        expFormat = expFormat.replace(/([\^\$\.\*\+\?\|\[\]\(\)\{\}])/g, "\\\\$1");
        const regexp: string[] = ["^"];
        const groups = [];
        let index = 0,
            quoteCount = 0;
        const tokenRegExp = /\/|dddd|ddd|dd|d|MMMM|MMM|MM|M|yyyy|yy|y|hh|h|HH|H|mm|m|ss|s|tt|t|fff|ff|f|zzz|zz|z|gg|g/g;
        let match;
        while ((match = tokenRegExp.exec(expFormat)) !== null) {
            const preMatch = expFormat.slice(index, match.index);
            index = tokenRegExp.lastIndex;
            quoteCount += _appendPreOrPostMatch(preMatch, regexp);
            if (quoteCount % 2 === 1) {
                regexp.push(match[0]);
                continue;
            }
            switch (match[0]) {
                case "MMMM":
                case "MMM":
                    regexp.push("([\\d\\D]+)");
                    break;
                case "dddd":
                case "ddd":
                case "gg":
                case "g":
                    regexp.push("(\\D+)");
                    break;
                case "tt":
                case "t":
                    regexp.push("(\\D*)");
                    break;
                case "yyyy":
                    regexp.push("(\\d{4})");
                    break;
                case "fff":
                    regexp.push("(\\d{3})");
                    break;
                case "ff":
                    regexp.push("(\\d{2})");
                    break;
                case "f":
                    regexp.push("(\\d)");
                    break;
                case "dd":
                case "d":
                case "MM":
                case "M":
                case "yy":
                case "y":
                case "HH":
                case "H":
                case "hh":
                case "h":
                case "mm":
                case "m":
                case "ss":
                case "s":
                    regexp.push("(\\d\\d?)");
                    break;
                case "zzz":
                    regexp.push("([+-]?\\d\\d?:\\d{2})");
                    break;
                case "zz":
                case "z":
                    regexp.push("([+-]?\\d\\d?)");
                    break;
                case "/":
                    regexp.push("(\\" + dtf.DateSeparator + ")");
                    break;
                default:
                    throw new Error("Invalid date format pattern.");
            }
            groups.push(match[0]);
        }
        _appendPreOrPostMatch(expFormat.slice(index), regexp);
        regexp.push("$");
        const regexpStr = regexp.join("").replace(/\s+/g, "\\s+");
        const parseRegExp = { regExp: regexpStr, groups: groups };
        (dtf as any)._parseRegExp[format] = parseRegExp;
        return parseRegExp;
    }

    function _indexOf(array: any[], item: any): number {
        if (typeof item === "undefined") {
            return -1;
        }
        for (let i = 0, length = array.length; i < length; i++) {
            if (typeof array[i] !== "undefined" && array[i] === item) {
                return i;
            }
        }
        return -1;
    }
    function _getIndex(value: any, a1: any, a2: any) {
        const upper = _toUpper(value);
        let i = _indexOf(a1, upper);
        if (i === -1) {
            i = _indexOf(a2, upper);
        }
        return i;
    }
    function _getMonthIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperMonths) {
            (cultureInfo as any)._upperMonths = _toUpperArray(cultureInfo.dateTimeFormat.MonthNames);
            (cultureInfo as any)._upperMonthsGenitive = _toUpperArray(cultureInfo.dateTimeFormat.MonthGenitiveNames);
        }
        return _getIndex(value, (cultureInfo as any)._upperMonths, (cultureInfo as any)._upperMonthsGenitive);
    }
    function _getAbbrMonthIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperAbbrMonths) {
            (cultureInfo as any)._upperAbbrMonths = _toUpperArray(cultureInfo.dateTimeFormat.AbbreviatedMonthNames);
            (cultureInfo as any)._upperAbbrMonthsGenitive = _toUpperArray(cultureInfo.dateTimeFormat.AbbreviatedMonthGenitiveNames);
        }
        return _getIndex(value, (cultureInfo as any)._upperAbbrMonths, (cultureInfo as any)._upperAbbrMonthsGenitive);
    }
    function _getDayIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperDays) {
            (cultureInfo as any)._upperDays = _toUpperArray(cultureInfo.dateTimeFormat.DayNames);
        }
        return _indexOf((cultureInfo as any)._upperDays, _toUpper(value));
    }
    function _getAbbrDayIndex(cultureInfo: ICultureInfo, value: any) {
        if (!(cultureInfo as any)._upperAbbrDays) {
            (cultureInfo as any)._upperAbbrDays = _toUpperArray(cultureInfo.dateTimeFormat.AbbreviatedDayNames);
        }
        return _indexOf((cultureInfo as any)._upperAbbrDays, _toUpper(value));
    }
    function _toUpperArray(arr: any) {
        const result = [];
        for (let i = 0, il = arr.length; i < il; i++) {
            result[i] = _toUpper(arr[i]);
        }
        return result;
    }
    function _toUpper(value: any) {
        return value.split("\u00A0").join(" ").toUpperCase();
    }
}

/**
 * Strip the time from the given date (return a new date) such that the new date is of 12:00:00 AM
 */
export function stripTimeFromDate(date: Date): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Return a Date object with the UTC time of the supplied Date
 */
export function getDateInUTC(date: Date): Date {
    return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

/**
 * Return a Date object with the local time of the supplied Date
 * Assumes the passed date is in UTC
 */
export function getDateInLocal(date: Date): Date {
    return new Date(date.getTime() - date.getTimezoneOffset() * 60 * 1000);
}

/**
 * Compares two date objects. Returns a number:
 *    Less than 0 if date1 is earlier than date2
 *    Zero if date1 is the same as date2
 *    Greater than zero if date1 is later than date2
 *
 * If an argument is not an instance of a Date then it is considered earlier than
 * the other argument, or the same if the other argument is also not an instance of
 * a Date
 *
 * @param date1 Date object to compare
 * @param date2 Date object to compare
 * @return
 */
export function defaultComparer(date1: Date, date2: Date): number {
    if (date1 instanceof Date && date2 instanceof Date) {
        return date1.getTime() - date2.getTime();
    }

    if (date1 instanceof Date) {
        return 1;
    }

    if (date2 instanceof Date) {
        return -1;
    }

    // Both date objects are not dates: consider them equal
    return 0;
}

/**
 * Converts a time from the client (e.g. new Date()) to the user's preferred timezone
 *
 * @param date The Date object to convert
 *
 */
export function getDateInUserTimeZone(date?: Date): Date {
    return convertBetweenClientAndUserTime(date ?? new Date(), true, true);
}

/**
 * Converts a time from the user's preferred timezone to the client (e.g. new Date()) timezone
 *
 * @param date The Date object to convert
 */
export function getDateInClientTimeZone(date: Date): Date {
    return convertBetweenClientAndUserTime(date, true, false);
}

function convertBetweenClientAndUserTime(date: Date, adjustOffset: boolean, toUserTime: boolean): Date {
    const localTime = date.getTime();
    const localOffset = date.getTimezoneOffset() * 60000;
    let newUtc: number;

    let offset = getUtcOffset();

    if (adjustOffset) {
        offset = getOffsetForDate(date);
    }

    // Adjust client's time to utc, and then adjust to appropriate timezone
    if (toUserTime) {
        newUtc = localTime + localOffset + offset;
    } else {
        newUtc = localTime - localOffset - offset;
    }

    let clientDate = <Date>new Date(newUtc); // Explicit cast because of Date declare override at top of file

    if (adjustOffset && getOffsetForDate(clientDate) === getOffsetForDate(date)) {
        clientDate = adjustOffsetForTimes(date, clientDate);
    }

    return clientDate;
}

/**
 * Gets the offset of the date passed in.
 *
 * @param date The Date object for which the offset is required.
 * @param defaultToUtcOffset A value indicating whether the server side set utc offset should be returned if no offset for date is returned.
 */
export function getOffsetForDate(date: Date): number {
    const timeZoneMap = getTimeZoneMap();

    if (!timeZoneMap) {
        return getUtcOffset();
    }

    let utcDate = getUtcDate(date);

    // find the right offset for this date.
    for (let i = 0; i < timeZoneMap.length; i++) {
        if (i === timeZoneMap.length - 1 && utcDate === timeZoneMap[i].start) {
            return timeZoneMap[i].offset;
        } else if (i === timeZoneMap.length - 1) {
            // exit when no range is found
            break;
        }

        if (utcDate >= timeZoneMap[i].start && utcDate < timeZoneMap[i + 1].start) {
            return timeZoneMap[i].offset;
        }
    }

    // no offset found use the server side set offset for the current date.
    return getUtcOffset();
}

function getTimeZoneMap() {
    const legacyPageContext = getPageContext("ms.vss-web.legacy-platform-data-provider");

    if (legacyPageContext?.timeZonesConfiguration?.daylightSavingsAdjustments) {
        const timeZoneMap = legacyPageContext.timeZonesConfiguration.daylightSavingsAdjustments;
        for (const daylightSavingsAdjustment of timeZoneMap) {
            if (typeof daylightSavingsAdjustment.start === "string") {
                daylightSavingsAdjustment.start = new Date(daylightSavingsAdjustment.start);
            }
        }
        return timeZoneMap;
    }
}

function getUtcOffset() {
    const legacyPageContext = getPageContext("ms.vss-web.legacy-platform-data-provider");

    if (legacyPageContext?.globalization) {
        return legacyPageContext.globalization.timezoneOffset || 0;
    }

    return 0;
}

function getCurrentCultureFromLegacyContext(): ICultureInfo {
    const legacyPageContext = getPageContext("ms.vss-web.legacy-platform-data-provider");
    let currentCulture: ICultureInfo;
    let msAjaxConfig = legacyPageContext?.microsoftAjaxConfig;
    if (msAjaxConfig && msAjaxConfig.cultureInfo) {
        currentCulture = msAjaxConfig.cultureInfo;
        if (msAjaxConfig.cultureInfo.eras) {
            currentCulture.dateTimeFormat.eras = msAjaxConfig.cultureInfo.eras;
        }
    } else {
        currentCulture = getInvariantCulture();
    }
    return currentCulture;
}

enum HistoryGroupId {
    Today,
    Yesterday,
    LastSevenDays,
    LastThirtyDays,
    Older
}

export function getTimeStringFormat(updateDate: Date): string | undefined {
    const timeGroup = getTimeGroup(updateDate);
    return getTimeFormatMap().get(timeGroup);
}

function getTimeGroup(updateDate: Date): HistoryGroupId {
    const now = getDateInUserTimeZone();

    if (now.toDateString() === updateDate.toDateString()) {
        return HistoryGroupId.Today;
    }

    const diff = now.getTime() - updateDate.getTime();
    if (diff < week) {
        const yesterday = new Date();
        yesterday.setDate(now.getDate() - 1);
        if (updateDate.getDate() === yesterday.getDate()) {
            return HistoryGroupId.Yesterday;
        } else {
            return HistoryGroupId.LastSevenDays;
        }
    } else {
        return HistoryGroupId.Older;
    }
}

export function getExtendedTimeFormat(): string {
    const culture = getCurrentCultureFromLegacyContext();
    return "ddd " + culture.dateTimeFormat.ShortDatePattern + " " + culture.dateTimeFormat.ShortTimePattern;
}

function getTimeFormatMap(): Map<HistoryGroupId, string> {
    const culture = getCurrentCultureFromLegacyContext();
    const result = new Map<HistoryGroupId, string>();
    result.set(HistoryGroupId.Today, culture.dateTimeFormat.ShortTimePattern);
    result.set(HistoryGroupId.Yesterday, "ddd " + culture.dateTimeFormat.ShortTimePattern);
    result.set(
        HistoryGroupId.LastSevenDays,
        culture.name === "en-US" && startsWith(culture.dateTimeFormat.ShortDatePattern, "d", true)
            ? "ddd M/d"
            : "ddd " + culture.dateTimeFormat.ShortDatePattern
    );
    result.set(HistoryGroupId.LastThirtyDays, "d");
    result.set(HistoryGroupId.Older, "d");
    return result;
}

/**
 * Adjusts the time zone offset by applying the time difference in the offsets.
 *
 * @param oldDate The Date object which was used before time zone changed.
 * @param newDate The Date object which was used after time zone changed.
 */
export function adjustOffsetForTimes(oldDate: Date, newDate: Date): Date {
    // this function should be used if the offset between the old date and new date is different
    // and their difference is to be applied to the applied date passed in.
    // for eg. if the oldDate is March 11 00:00 AM 7GMT and the newDate is March 9 23:00 PM 8GMT
    // then there is a offset difference of 1 hour. This will happen when you use addDays(oldDate, -1)
    // because of the offset difference the result you will get March 9 23:00 PM 8GMT instead of March 10 00:00 AM 8GMT
    // hence the offset is applied to March 9 23:00 PM to get March 10 00:00 AM 8GMT

    let setDate = newDate;

    const oldOffset = oldDate.getTimezoneOffset();
    const newOffset = newDate.getTimezoneOffset();
    if (oldOffset !== newOffset) {
        setDate = addDays(setDate, (newOffset - oldOffset) / (60 * 24));
    }

    return setDate;
}

/**
 * Add days to a date. This is DST safe since it doesn't use getTime() for the adjustment.
 * @param date The date to add days to
 * @param days Number of days to add
 */
export function addDays(date: Date, days: number): Date {
    return new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate() + days,
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
    );
}

/**
 * Convert a date object to UTC timezone.
 * @param localDate The date object to convert
 */
export function getUtcDate(localDate: Date): Date {
    return new Date(
        localDate.getUTCFullYear(),
        localDate.getUTCMonth(),
        localDate.getUTCDate(),
        localDate.getUTCHours(),
        localDate.getUTCMinutes(),
        localDate.getUTCSeconds(),
        localDate.getUTCMilliseconds()
    );
}
